import React from 'react';
import V2Layout from '../Layout';
import { Box, Button, Card, Checkbox, Chip, Grid, InputAdornment, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from '@mui/material';
import { translate } from '../../../i18n/customI18nProvider';
import Swal from 'sweetalert2';
import { convertMessageCodeToMessage } from '../../../util';
import { getRight } from '../../../services/storage';
import { createSubPacket, generateQrsFile, getCategoryDetails, getPacketDetails, getWristbands } from '../../../services/wristbandCategory.service';

import SearchIcon from '@mui/icons-material/Search';
import { removeUrlParam, setUrlParam } from '../../../services/common.service';
import StyledTableRow from '../../../components/v2/common/table/StyledTableRow';
import {visuallyHidden} from '@mui/utils';
import config from '../../../config';
import { Backpack } from '@mui/icons-material';

const tableHeader = [
    {
        id: 'bulk_actions',
        key: 'bulk_actions',
        align: 'left',
        disablePadding: false,
        label: '',
        sortable: false
    },
    {
        id: 'number',
        key: 'number',
        align: 'left',
        disablePadding: false,
        label: translate('custom.number'),
        sortable: true
    },
    {
        id: 'uid',
        key: 'uid',
        align: 'left',
        disablePadding: false,
        label: translate('custom.uid'),
        sortable: true
    }
]


class ShowWristbandPacket extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            current_right: getRight(),
            loading: false,
            form_error: {},
            wristbands: [],
            selected_wristbands: [],
            count: 0,
            skip: 0,
            limit: 50,
            limitOptions: [10, 50, 100, 250, 500],
            sort_key: '',
            sort_order: '',
            search: '',
            number: '',
        }
    }

    async setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve);
        });
    }

    async componentDidMount() {
        let category_id = this.props?.match?.params?.category_id ? this.props.match.params.category_id : null;
        let packet_id = this.props?.match?.params?.packet_id ? this.props.match.params.packet_id : null;

        const urlParams = new URLSearchParams(window.location.search);
        const search = urlParams.get('search') ? urlParams.get('search').replaceAll('"', '') : '';
        const number = urlParams.get('number') ? urlParams.get('number').replaceAll('"', '') : '';

        await this.setStateAsync({
            loading: true,
            category_id,
            packet_id,
            search: search,
            number: number
        });

        await this.getCategory();

        await this.getPacket();

        await this.getWristbands();
            
        
        this.setState({ loading: false });
    }   

    async getCategory() {
        let category = await getCategoryDetails(this.state.category_id);
        if (category.error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: category.error_code ? convertMessageCodeToMessage(category.error_code) : category.error
            });
            return;
        }

        await this.setStateAsync({
            category_name: category.name,
            title: translate('custom.wristband_packet') + ' - ' + category.name
        });
    }

    async getPacket() {
        let packet = await getPacketDetails(this.state.category_id, this.state.packet_id);
        if (packet.error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: packet.error_code ? convertMessageCodeToMessage(packet.error_code) : packet.error
            });
            return;
        }

        await this.setStateAsync({
            shortId: packet.shortId,
            importType: packet.import?.type,
            importId: packet.import?.id,
            importInfo: packet.import?.otherInfo
        });
    }

    async getWristbands() {
        let options = {
            skip: this.state.skip,
            limit: this.state.limit
        }

        const filter = {};

        if (this.state.search && this.state.search.length > 0) {
            filter.uid = this.state.search;
        }

        if (this.state.number && this.state.number.length > 0) {
            filter.number = this.state.number;
        }

        if (Object.keys(filter).length > 0) {
            options.filter = encodeURIComponent(JSON.stringify(filter));
        }
        
        if (this.state.sort_key) {
            options.sort = encodeURIComponent(JSON.stringify([this.state.sort_key, this.state.sort_order]));
        }

        let {wristbands, total} = await getWristbands(options, this.state.category_id, this.state.packet_id);

        this.setState({
            wristbands: wristbands,
            selected_wristbands: [],
            count: total
        });
    }

    async createNewPacket() {
        const confirm_delete = await Swal.fire({
            icon: 'warning',
            title: translate('custom.warning'),
            text: translate('custom.are_you_sure_you_want_to_create_a_new_packet') + ' ' + translate('custom.selected_wristbands_will_be_remove_from_this_packet'),
            showCancelButton: true,
            confirmButtonText: translate('custom.ok'),
            cancelButtonText: translate('custom.cancel'),
            confirmButtonColor: config.baseColor
        });
        if(!confirm_delete.isConfirmed) return

        try {
            let response = await createSubPacket(this.state.packet_id, this.state.selected_wristbands);
            if (response.error) {
                Swal.fire({
                    icon: 'error',
                    title: translate('custom.error'),
                    text: response.error_code ? convertMessageCodeToMessage(response.error_code) : response.error
                });
                return;
            }

            Swal.fire({
                icon: 'success',
                title: translate('custom.success'),
                text: translate('custom.new_packet_created_successfully') + ' (' + response.shortId + ')'
            });

            const file = await generateQrsFile(this.state.category_name, [response]);
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Refresh the wristbands list
            this.getWristbands();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: error.message
            });
        }
    }

    async changeMultipleSelection(e, row) {
        const shiftPressed = e.nativeEvent.shiftKey;             
        if (e.target.checked) {
            if(shiftPressed && this.state.selected_wristbands.length > 0) {
                const lastSelectedId = this.state.selected_wristbands[this.state.selected_wristbands.length - 1];
                const lastSelectedIndex = this.state.wristbands.findIndex(wristband => wristband._id === lastSelectedId);
                const currentIndex = this.state.wristbands.findIndex(wristband => wristband._id === row._id);
                
                const start = Math.min(lastSelectedIndex, currentIndex);
                const end = Math.max(lastSelectedIndex, currentIndex);
                
                const newSelections = this.state.wristbands.slice(start, end + 1)
                    .map(wristband => wristband._id)
                    .filter(id => !this.state.selected_wristbands.includes(id));
                
                this.setState({
                    selected_wristbands: [...this.state.selected_wristbands, ...newSelections]
                });
            } else {
                this.setState({
                    selected_wristbands: [...this.state.selected_wristbands, row._id]
                });
            }
        } else {
            this.setState({
                selected_wristbands: this.state.selected_wristbands.filter((id) => id !== row._id)
            });
        }
    }

    render() {
        return (
            <V2Layout
                currentMenu='wristbands'
                title={this.state.title}
            >

                {(this.state.loading) ? (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                    </Box>
                ) : null}

                <Paper sx={{mt: 2, padding: 2}}>
                    {/* Packet Info */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                            <Box>
                                <strong>{translate('custom.short_id')}:</strong> {this.state.shortId}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Box>
                                <strong>{translate('custom.import_type')}:</strong> {this.state.importType}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Box>
                                <strong>{translate('custom.import_id')}:</strong> {this.state.importId}
                            </Box>
                        </Grid>
                        {(this.state.importInfo) ?
                            <Grid item xs={12} sm={3}>
                                <Box>
                                    <strong>{translate('custom.import_info')}:</strong> {this.state.importInfo}
                                </Box>
                            </Grid>
                        : null}
                    </Grid>

                    {/* Wristbands Table */}
                    <Grid container spacing={2} style={{marginTop: '5px', marginBottom: '10px'}}>
                        <Grid item>
                            <TextField
                                size='small'
                                variant='outlined'
                                fullWidth
                                id="search-input"
                                label={translate('custom.uid')}
                                placeholder={translate('custom.search')}
                                value={this.state.search}
                                onChange={async (e) => {
                                    await this.setStateAsync({search: e.target.value});
                                    (e?.target?.value?.length > 0) ? setUrlParam('search', e.target.value) : removeUrlParam('search');
                                    this.getWristbands();
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                }}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                size='small'
                                variant='outlined'
                                fullWidth
                                id="search-input"
                                label={translate('custom.number')}
                                placeholder={translate('custom.search')}
                                value={this.state.number}
                                onChange={async (e) => {
                                    await this.setStateAsync({number: e.target.value});
                                    (e?.target?.value?.length > 0) ? setUrlParam('number', e.target.value) : removeUrlParam('number');
                                    this.getWristbands();
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                }}
                            />
                        </Grid>
                    </Grid>
                    <TableContainer component={Card} style={{maxHeight: '80vh', overflow: 'auto'}}>
                        {(this.state.selected_wristbands.length > 0) ? (
                            // The below component should be displayed over the table and should not make the table move below
                            <Grid container style={{padding: '10px', justifyContent: 'space-between' }}>
                                <Grid item>
                                    {/* show the selected count with cross icon to select all */}
                                    <Chip
                                        label={this.state.selected_wristbands.length + ' ' + translate('custom.selected')}
                                        onDelete={() => {
                                            this.setState({
                                                selected_wristbands: []
                                            });
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button
                                        size='small'
                                        onClick={() => {
                                            this.createNewPacket()
                                        }}
                                        startIcon={<Backpack />}
                                        color='primary'
                                    >
                                        {translate('custom.create_new_packet')}
                                    </Button>
                                </Grid>
                            </Grid>
                        ) : null}
                        <Table stickyHeader size='small'>
                            <TableHead>
                                <TableRow>
                                    {tableHeader.map((headCell) => (
                                        <TableCell
                                            key={headCell.key}
                                            align={headCell.align ? headCell.align : 'left'}
                                            padding={headCell.disablePadding ? 'none' : 'normal'}
                                            sortDirection={(this.state.sort_key === headCell.key) ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : false}
                                        >                                            
                                            {(headCell.key === 'bulk_actions') ? (
                                                <Checkbox
                                                    style={{padding: 0}}
                                                    checked={this.state.wristbands.length && (this.state.selected_wristbands.length === this.state.wristbands.length)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            this.setState({
                                                                selected_wristbands: this.state.wristbands.map((wristband) => wristband._id)
                                                            });
                                                        } else {
                                                            this.setState({
                                                                selected_wristbands: []
                                                            });
                                                        }
                                                    }}
                                                />
                                            ) : null}
                                            {(headCell.sortable) ? (
                                                <TableSortLabel
                                                    active={this.state.sort_key === headCell.key}
                                                    direction={this.state.sort_key === headCell.key ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : 'asc'}
                                                    onClick={async () => {
                                                        await this.setState({
                                                            sort_key: headCell.key,
                                                            sort_order: this.state.sort_key === headCell.key ? (this.state.sort_order === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
                                                        })
                                                        this.getWristbands();
                                                    }}
                                                >
                                                    {headCell.label}
                                                    {this.state.sort_key === headCell.key ? (
                                                        <Box component="span" sx={visuallyHidden}>
                                                            {this.state.sort_order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            ) : (
                                                <>
                                                    {headCell.label}
                                                </>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(this.state.wristbands && Array.isArray(this.state.wristbands)) ? this.state.wristbands.map((row) => (
                                    <StyledTableRow key={row._id} hover>
                                        <TableCell>
                                            <Checkbox
                                                style={{padding: 0}}
                                                checked={this.state.selected_wristbands.includes(row._id)}
                                                onChange={(e) => this.changeMultipleSelection(e, row)}
                                            />
                                        </TableCell>
                                        <TableCell>{row.number}</TableCell>
                                        <TableCell>{row.uid}</TableCell>
                                    </StyledTableRow>
                                ))
                                :
                                <TableRow>
                                    <TableCell colSpan={tableHeader.length} align="center" style={{padding: '20px'}}>
                                        {translate('custom.no_data')}
                                    </TableCell>
                                </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Grid container style={{width: '100%'}}>
                        <Grid item xs={12}> 
                            <TablePagination
                                component="div"
                                count={this.state.count}
                                page={this.state.skip / this.state.limit}
                                onPageChange={async (event, page) => {
                                    await this.setStateAsync({
                                        skip: page * this.state.limit
                                    })
                                    this.getWristbands();
                                }}
                                rowsPerPage={this.state.limit}
                                onRowsPerPageChange={async (event) => {
                                    await this.setStateAsync({
                                        limit: event.target.value
                                    })
                                    this.getWristbands();
                                }}
                                rowsPerPageOptions={this.state.limitOptions}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </V2Layout>
        );
    }
}

export default ShowWristbandPacket;